import React from 'react';
import { SEO } from '../components/SEO';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { RocketIcon, GraduationCapIcon, UsersIcon, HomeIcon, ArrowRightIcon, MailIcon, CheckCircleIcon } from 'lucide-react';
import { motion } from 'framer-motion';
export const SollicitatiePage: React.FC = () => {
  const benefits = [{
    icon: <RocketIcon className="h-6 w-6" data-id="element-2088" />,
    title: 'Inspirerende projecten',
    description: 'Werk aan websites en digitale oplossingen voor uiteenlopende bedrijven.'
  }, {
    icon: <GraduationCapIcon className="h-6 w-6" data-id="element-2089" />,
    title: 'Ruimte voor ontwikkeling',
    description: 'Leer nieuwe skills en groei in je rol. Wij investeren in jouw toekomst.'
  }, {
    icon: <UsersIcon className="h-6 w-6" data-id="element-2090" />,
    title: 'Hecht team',
    description: 'Klein team, korte lijnen. Jouw ideeën tellen mee vanaf dag één.'
  }, {
    icon: <HomeIcon className="h-6 w-6" data-id="element-2091" />,
    title: 'Hybride werken',
    description: 'Flexibel werken vanuit kantoor of thuis. Wij vertrouwen op resultaat.'
  }];
  const vacancies = [{
    title: 'Stage Design',
    tags: ['UI/UX', 'Figma', 'Webdesign', 'Branding'],
    type: 'Stage',
    slug: 'stage-design',
    status: 'bezet'
  }, {
    title: 'Stage Account Manager',
    tags: ['Sales', 'Klantcontact', 'Communicatie', 'Project coördinatie'],
    type: 'Stage',
    slug: 'stage-account-manager',
    status: 'open'
  }, {
    title: 'Stage Developer',
    tags: ['React', 'TypeScript', 'WordPress', 'Tailwind CSS'],
    type: 'Stage',
    slug: 'stage-developer',
    status: 'binnenkort',
    opensAt: '2026-09-05T00:00:00'
  }];
  const processSteps = [{
    title: 'Kennismaking',
    description: 'We plannen een informeel gesprek om elkaar te leren kennen.'
  }, {
    title: 'Opdracht & gesprek',
    description: 'Je maakt een kleine opdracht en we bespreken je aanpak.'
  }, {
    title: 'Aanbod',
    description: 'Past het? Dan doen we je een aanbod waar je blij van wordt.'
  }];
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-2092">
      <SEO title="Werken bij Noord Digital | Vacatures & Stages" description="Kom werken bij Noord Digital. Bouw mee aan websites, SEO en hosting voor ambitieuze bedrijven. Bekijk onze vacatures en stageplekken." keywords="werken bij noord digital, vacatures webdeveloper, stage webdesign, seo specialist vacature, erkend leerbedrijf" canonical="https://noorddigital.nl/sollicitatie" data-id="element-2093" />
      <Header data-id="element-2094" />

      {/* Hero Section */}
      <div className="w-full bg-gradient-to-br from-[#007AFF] to-[#1B2B44] py-16 md:py-24 relative overflow-hidden" data-id="element-2095">
        <div className="absolute inset-0 opacity-20" data-id="element-2096">
          <div className="w-full h-full bg-[url('https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80')] bg-cover bg-center" data-id="element-2097"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] relative z-10" data-id="element-2098">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-2099">
            {/* Left side - Main content */}
            <div className="lg:col-span-7" data-id="element-2100">
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="font-bold text-white leading-tight mb-6 text-4xl md:text-5xl lg:text-6xl" data-id="element-2101">
                Kom werken bij Noord Digital
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-white text-opacity-90 mb-8 text-lg md:text-xl leading-relaxed max-w-2xl" data-id="element-2102">
                Bouw mee aan websites, SEO en hosting voor ambitieuze bedrijven.
                Wij zoeken talent dat wil groeien.
              </motion.p>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="flex flex-wrap gap-4" data-id="element-2103">
                <a href="#vacatures" className="inline-flex items-center px-8 py-4 bg-white text-[#007AFF] font-bold rounded-xl hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl" data-id="element-2104">
                  Bekijk vacatures
                </a>
              </motion.div>
            </div>

            {/* Right side - Visuals */}
            <div className="lg:col-span-5" data-id="element-2105">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="bg-white rounded-3xl p-8 shadow-2xl relative flex flex-col items-center text-center" data-id="element-2106">
                <div className="w-32 h-32 bg-gradient-to-br from-[#007AFF] to-[#005FCC] rounded-full mb-6 p-2 shadow-inner border-4 border-white" data-id="element-2107">
                  <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden mascotte" className="w-full h-full object-cover rounded-full" data-id="element-2108" />
                </div>

                <h3 className="font-bold text-[#111827] text-xl mb-4" data-id="element-2109">
                  Word onderdeel van ons team
                </h3>

                <div className="w-full h-px bg-gray-100 my-4" data-id="element-2110"></div>

                <img src="https://cdn.magicpatterns.com/uploads/1aHtpkWESLeaWQNYKA51qz/WhatsApp_Image_2026-02-28_at_14.34.29.jpg" alt="S-BB Erkend Leerbedrijf" className="w-48 h-auto object-contain mt-2" data-id="element-2111" />
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Waarom Noord Digital Section */}
      <div className="w-full bg-white py-20 md:py-24" data-id="element-2112">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2113">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-2114">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-2115">
              Waarom werken bij Noord Digital?
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2116">
            {benefits.map((benefit, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-[#F7F8FA] rounded-2xl p-8 hover:shadow-md transition-all duration-300 border border-transparent hover:border-gray-200" data-id="element-2117">
                <div className="flex items-start gap-5" data-id="element-2118">
                  <div className="flex-shrink-0 w-14 h-14 bg-[#007AFF] bg-opacity-10 rounded-xl flex items-center justify-center text-[#007AFF]" data-id="element-2119">
                    {benefit.icon}
                  </div>
                  <div data-id="element-2120">
                    <h3 className="font-bold text-[#111827] text-xl mb-3" data-id="element-2121">
                      {benefit.title}
                    </h3>
                    <p className="text-[#6B7280] leading-relaxed" data-id="element-2122">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>

      {/* Openstaande Vacatures Section */}
      <div id="vacatures" className="w-full bg-[#F7F8FA] py-20 md:py-24" data-id="element-2123">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2124">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-2125">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-2126">
              Openstaande vacatures
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-[#6B7280] text-lg leading-relaxed" data-id="element-2127">
              Zit jouw droombaan er niet tussen? Een open sollicitatie sturen
              mag altijd!
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2128">
            {vacancies.map((vacancy, index) => {
            // Calculate simple countdown for 'binnenkort'
            let countdownText = '';
            if (vacancy.status === 'binnenkort' && vacancy.opensAt) {
              const targetDate = new Date(vacancy.opensAt);
              const now = new Date();
              const diffTime = Math.abs(targetDate.getTime() - now.getTime());
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              const months = Math.floor(diffDays / 30);
              const days = diffDays % 30;
              countdownText = `Opent over ${months > 0 ? `${months} maanden en ` : ''}${days} dagen`;
            }
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} whileHover={{
              y: -5
            }} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 flex flex-col h-full relative overflow-hidden" data-id="element-2129">
                  <div className="mb-6" data-id="element-2130">
                    <div className="flex justify-between items-start mb-4" data-id="element-2131">
                      <span className="inline-block bg-blue-50 text-[#007AFF] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide" data-id="element-2132">
                        {vacancy.type}
                      </span>
                      {vacancy.status === 'bezet' && <span className="inline-block bg-gray-100 text-gray-600 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide" data-id="element-2133">
                          Op dit moment bezet
                        </span>}
                      {vacancy.status === 'open' && <span className="inline-block bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide" data-id="element-2134">
                          Nu open
                        </span>}
                      {vacancy.status === 'binnenkort' && <span className="inline-block bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide" data-id="element-2135">
                          Binnenkort open
                        </span>}
                    </div>
                    <h3 className="font-bold text-[#111827] text-2xl mb-4" data-id="element-2136">
                      {vacancy.title}
                    </h3>
                    <div className="flex flex-wrap gap-2 mb-4" data-id="element-2137">
                      {vacancy.tags.map((tag, tagIndex) => <span key={tagIndex} className="bg-gray-100 text-gray-600 text-sm px-3 py-1 rounded-md" data-id="element-2138">
                          {tag}
                        </span>)}
                    </div>
                    {vacancy.status === 'binnenkort' && <div className="bg-amber-50 text-amber-800 text-sm p-3 rounded-lg border border-amber-100 font-medium" data-id="element-2139">
                        ⏳ {countdownText}
                      </div>}
                  </div>

                  <div className="mt-auto pt-8" data-id="element-2140">
                    <a href={`/vacature/${vacancy.slug}`} className={`flex items-center justify-center w-full font-semibold py-3 px-6 rounded-lg transition duration-300 group ${vacancy.status === 'open' ? 'bg-[#007AFF] hover:bg-[#005FCC] text-white' : 'bg-gray-100 hover:bg-gray-200 text-[#111827]'}`} data-id="element-2141">
                      {vacancy.status === 'open' ? 'Bekijk vacature' : 'Bekijk details'}
                      <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2142" />
                    </a>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </div>

      {/* Onze Cultuur Section */}
      <div className="w-full bg-white py-20 md:py-24" data-id="element-2143">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2144">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2145">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-2146">
              <h2 className="font-bold text-[#111827] mb-6 text-3xl md:text-4xl" data-id="element-2147">
                Onze cultuur
              </h2>
              <p className="text-[#6B7280] text-lg leading-relaxed mb-6" data-id="element-2148">
                Bij Noord Digital geloven we in een open, eerlijke en gedreven
                werksfeer. We werken hard voor onze klanten, maar vieren ook
                samen onze successen. Fouten maken mag, zolang we er maar van
                leren.
              </p>
              <p className="text-[#6B7280] text-lg leading-relaxed mb-8" data-id="element-2149">
                We zijn een jong en dynamisch team dat altijd op zoek is naar de
                nieuwste ontwikkelingen in webdesign, SEO en techniek. Kennis
                delen staat bij ons centraal.
              </p>

              <div className="bg-[#F7F8FA] rounded-2xl p-6 border border-gray-100 flex items-start gap-6" data-id="element-2150">
                <img src="https://cdn.magicpatterns.com/uploads/1aHtpkWESLeaWQNYKA51qz/WhatsApp_Image_2026-02-28_at_14.34.29.jpg" alt="S-BB Erkend Leerbedrijf" className="w-24 h-auto object-contain flex-shrink-0" data-id="element-2151" />
                <div data-id="element-2152">
                  <h4 className="font-bold text-[#111827] mb-2" data-id="element-2153">
                    Erkend Leerbedrijf
                  </h4>
                  <p className="text-[#6B7280] text-sm leading-relaxed" data-id="element-2154">
                    Noord Digital is een erkend leerbedrijf. Wij leiden
                    vakmensen op en bieden uitdagende stage- en leerplekken waar
                    je écht impact kunt maken.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="relative h-[400px] rounded-3xl overflow-hidden shadow-2xl" data-id="element-2155">
              <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80" alt="Team Noord Digital aan het werk" className="w-full h-full object-cover" data-id="element-2156" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" data-id="element-2157"></div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Sollicitatieproces Section */}
      <div className="w-full bg-[#F7F8FA] py-20 md:py-24 overflow-hidden" data-id="element-2158">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2159">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-2160">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-2161">
              Hoe verloopt het sollicitatieproces?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-[#6B7280] text-lg leading-relaxed" data-id="element-2162">
              We houden het graag simpel en persoonlijk. Geen eindeloze rondes,
              maar snel duidelijkheid.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-2163">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-gray-200 -translate-y-1/2 z-0" data-id="element-2164"></div>

            {processSteps.map((step, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2
          }} className="relative z-10 bg-white rounded-2xl p-8 text-center shadow-lg border border-gray-100" data-id="element-2165">
                <div className="w-16 h-16 mx-auto bg-[#007AFF] text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6 shadow-md border-4 border-white" data-id="element-2166">
                  0{index + 1}
                </div>
                <h3 className="font-bold text-[#111827] text-xl mb-3" data-id="element-2167">
                  {step.title}
                </h3>
                <p className="text-[#6B7280] leading-relaxed" data-id="element-2168">
                  {step.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="w-full bg-gradient-to-br from-[#007AFF] to-[#1B2B44] py-20 md:py-24 relative overflow-hidden" data-id="element-2169">
        <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.1)_1px,transparent_1px)] [background-size:24px_24px] opacity-30" data-id="element-2170"></div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] relative z-10" data-id="element-2171">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2172">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-bold text-white mb-6 text-3xl md:text-4xl lg:text-5xl" data-id="element-2173">
              Klaar om te solliciteren?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-white text-opacity-90 text-lg md:text-xl leading-relaxed mb-10" data-id="element-2174">
              Stuur je CV en motivatie naar ons op. We nemen binnen 2 werkdagen
              contact met je op.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-2175">
              <a href="mailto:sollicitatie@noorddigital.nl" className="inline-flex items-center px-8 py-4 bg-white text-[#007AFF] font-bold rounded-xl hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl w-full sm:w-auto justify-center" data-id="element-2176">
                <MailIcon className="mr-2 h-5 w-5" data-id="element-2177" />
                Solliciteer direct
              </a>
              <a href="/websites" className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-xl hover:bg-white hover:text-[#007AFF] transition-all w-full sm:w-auto justify-center" data-id="element-2178">
                Bekijk onze diensten
              </a>
            </motion.div>
          </div>
        </div>
      </div>

      <Footer data-id="element-2179" />
    </div>;
};